import { dayjs } from '@/utils/dayjs';
import { pluralize } from '@/utils/string';

const capitalize = <T extends string>(s: T): Capitalize<T> =>
  (s.charAt(0).toUpperCase() + s.slice(1)) as Capitalize<T>;

export const durationOptions = [
  'hours',
  'days',
  'weeks',
  'months',
  'years',
] as const;
export type DurationFragment = (typeof durationOptions)[number];

// Fragments are stored in their plural form, so a fragment rendered next to a
// count has to be singularized back for a count of exactly 1 ("1 month").
export const durationFragmentLabel = (
  unit: DurationFragment,
  value: number
): string => pluralize(unit.slice(0, -1), value);

export const isDateValid = (date?: unknown): date is Date =>
  date instanceof Date && date.getFullYear() > 1980;

export const addDays = (base: Date, days: number): Date => {
  const result = new Date(base);
  result.setDate(result.getDate() + days);
  return result;
};

export const addMs = (base: Date, ms: number): Date => {
  const result = new Date(base);
  result.setMilliseconds(result.getMilliseconds() + ms);
  return result;
};

/**
 *
 * fromDaysToBiggestAsPossible(0) // {unit: 'days', value: 0}
 * fromDaysToBiggestAsPossible(365) // {unit: 'years', value: 1}
 * fromDaysToBiggestAsPossible(364) // {unit: 'days', value: 364} - instead of 52 weeks
 * fromDaysToBiggestAsPossible(357) // {unit: 'weeks', value: 51}
 * fromDaysToBiggestAsPossible(330) // {unit: 'months', value: 11}
 * fromDaysToBiggestAsPossible(360) // {unit: 'days', value: 360} - instead of 12 months
 * fromDaysToBiggestAsPossible(720) // {unit: 'days', value: 720}
 * fromDaysToBiggestAsPossible(730) // {unit: 'years', value: 2}
 *
 **/
export function fromDaysToBiggestAsPossible(days: number): {
  unit: DurationFragment;
  value: number;
} {
  if (days === 0) {
    return { unit: 'days', value: 0 };
  }

  const years = Math.floor(days / 365);
  const remainingDays = days % 365;
  if (!remainingDays) {
    return { unit: 'years', value: years };
  }
  const monthsFromDays = Math.floor(remainingDays / 30);
  const remainingDaysFromMonths = remainingDays % 30;
  // checking (monthsFromDays % 12) so it won't return 12 months for 360 days
  if (!remainingDaysFromMonths && monthsFromDays % 12) {
    return { unit: 'months', value: years * 12 + monthsFromDays };
  }

  const weeksFromDays = Math.floor(remainingDays / 7);
  const remainingDaysFromWeeks = remainingDays % 7;
  // checking (weeksFromDays % 52) so it won't return 52 weeks for 364 days
  if (!remainingDaysFromWeeks && weeksFromDays % 52) {
    return { unit: 'weeks', value: years * 52 + weeksFromDays };
  }

  return { unit: 'days', value: days };
}

/**
 *
 * toDays('days', 0) // 0
 * toDays('days', 364) // 364
 * toDays('days', 365) // 365
 * toDays('weeks', 1) // 7
 * toDays('weeks', 51) // 357
 * toDays('months', 11) // 330
 * toDays('years', 1) // 365
 * toDays('years', 2) // 730
 * toDays('months', 12) // 365
 * toDays('months', 24) // 730
 * toDays('weeks', 52) // 365
 *
 **/
export function toDays(unit: DurationFragment, value: number): number {
  if (value === 0) {
    return 0;
  }

  if (unit === 'years') {
    return value * 365;
  }

  if (unit === 'months') {
    const years = Math.floor(value / 12);
    const remainingMonths = Math.floor(value % 12);
    return years * 365 + remainingMonths * 30;
  }

  if (unit === 'weeks') {
    const years = Math.floor(value / 52);
    const remainingWeeks = Math.floor(value % 52);
    return years * 365 + remainingWeeks * 7;
  }

  if (unit === 'hours') {
    return Math.floor(value / 24);
  }

  return value;
}

export function toFragment(unit: DurationFragment, value: number): number {
  const duration = dayjs.duration({
    days: value,
  });
  return Math.round(duration[`as${capitalize(unit)}` as DurationFragment]());
}

export const createDayJsTime = (
  hours: number,
  minutes: number,
  seconds?: number
) => {
  return dayjs
    .utc()
    .hour(hours)
    .minute(minutes)
    .second(seconds || 0)
    .millisecond(0);
};

export const formatDuration = (seconds: number, showSeconds = false) => {
  const duration = dayjs.duration(seconds, 'seconds');
  const hours = Math.floor(duration.asHours());
  const minutes = Math.floor(duration.asMinutes()) % 60;
  const secs = Math.floor(duration.asSeconds()) % 60;

  if (hours > 0) {
    return `${hours}h${minutes > 0 ? `, ${minutes}min` : ''}`;
  }
  if (!showSeconds) {
    return `${minutes}min`;
  }
  if (minutes > 0) {
    return `${minutes}min${secs > 0 ? `, ${secs}s` : ''}`;
  }
  return `${secs}s`;
};

/**
 *
 * formatDurationCompact(0) // 'Less than a minute'
 * formatDurationCompact(30) // 'Less than a minute'
 * formatDurationCompact(59) // 'Less than a minute'
 * formatDurationCompact(60) // '1m'
 * formatDurationCompact(120) // '2m'
 * formatDurationCompact(3600) // '1h 0m'
 * formatDurationCompact(3600 + 30 * 60) // '1h 30m'
 * formatDurationCompact(86400) // '1d 0h 0m'
 * formatDurationCompact(86400 + 3600 + 30 * 60) // '1d 1h 30m'
 *
 **/
export const formatDurationCompact = (seconds: number, showSeconds = false) => {
  const duration = dayjs.duration(seconds, 'seconds');

  const days = Math.floor(duration.asDays());
  const hours = Math.floor(duration.asHours()) % 24;
  const minutes = Math.floor(duration.asMinutes()) % 60;
  const secs = Math.floor(duration.asSeconds()) % 60;

  const parts: string[] = [];
  let hasStarted = false;

  if (days > 0) {
    parts.push(`${days}d`);
    hasStarted = true;
  }

  if (hasStarted || hours > 0) {
    parts.push(`${hours}h`);
    hasStarted = true;
  }

  if (hasStarted || minutes > 0) {
    parts.push(`${minutes}m`);
    hasStarted = true;
  }

  if (showSeconds) {
    parts.push(`${secs}s`);
  }

  if (!showSeconds && parts.length === 0) {
    parts.push('Less than a minute');
  }

  return parts.join(' ');
};

// ETA-style "X left" formatter — uses ceil so we never undershoot (89s → 2m left).
// Returns null when there's nothing meaningful to show.
export const formatEta = (etaSeconds: number): string | null => {
  if (etaSeconds <= 0) {
    return null;
  }
  if (etaSeconds < 60) {
    return `${etaSeconds}s left`;
  }
  const minutes = Math.ceil(etaSeconds / 60);
  if (minutes < 60) {
    return `${minutes}m left`;
  }
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return remMinutes === 0 ? `${hours}h left` : `${hours}h ${remMinutes}m left`;
};
